import { IGetMiddlewareSDK } from '@orbis-systems/omni-sdk-ts/dist/types/apis/middleware';
import { IGetOrbisSDK } from '@orbis-systems/omni-sdk-ts/dist/types/apis/orbis';
import 'reflect-metadata';
import { container, delay, instanceCachingFactory } from 'tsyringe';
import { DeepPartial } from 'utility-types';
import {
    AccountApi,
    BranchApi,
    DocumentApi,
    GroupApi,
    MarketApi,
    ModelApi,
    NewsApi,
    QuoteApi,
    ResearchApi,
    StatApi,
    TipranksApi,
    TradingApi,
    UserApi,
    WebSocketApi,
} from './api';
import { AxosAccountApi, AxosQuoteApi, AxosTradingApi } from './api/axos';
import {
    IAccountApi,
    IBranchApi,
    IDocumentApi,
    IGroupApi,
    IMarketApi,
    IModelApi,
    INewsApi,
    IStatApi,
    ITipranksApi,
    ITradingApi,
    IUserApi,
    IWebSocketApi,
} from './api/interfaces';
import { IQuoteApi } from './api/interfaces/IQuoteApi';
import { IResearchApi } from './api/interfaces/IResearchApi';
import { IWatchlistApi } from './api/interfaces/IWatchlistApi';
import { WatchlistApi } from './api/WatchlistApi';
import { MemoryCache } from './Cache';
import { ApiInjectToken, MiddlewareInjectToken } from './constants/tokens';
import { OrbisSdkWrapper } from './OrbisSdkWrapper';
import { CacheLevel, MiddlewareConfig, Sdks } from './types';

export * from './constants/lookups';
export * from './types';
export * from './utils';

export class OrbisApiMiddleware {
    branch!: IBranchApi;
    model!: IModelApi;
    group!: IGroupApi;
    user!: IUserApi;
    account!: IAccountApi;
    market!: IMarketApi;
    trading!: ITradingApi;
    quote!: IQuoteApi;
    watchlist!: IWatchlistApi;
    research!: IResearchApi;
    stat!: IStatApi;
    news!: INewsApi;
    tipranks!: ITipranksApi;
    webSocket!: IWebSocketApi;
    document!: IDocumentApi;

    private config: MiddlewareConfig = {
        cacheDuration: 5000,
        cacheLevel: CacheLevel.RequestsAndCalculations,
        apiPermissions: {
            advisor: false,
            rtbs: true,
            iav: true,
            documents: true,
            transfers: true,
            iavProcessorToken: true,
        },
    };

    constructor(
        private orbisSdk: IGetOrbisSDK,
        private middlewareSdk: IGetMiddlewareSDK,
        config: DeepPartial<MiddlewareConfig>,
    ) {
        this.initialize(config);
    }

    private registerDependencies() {
        const isAxos = this.config.appEnv?.startsWith('axos');

        container.register(MiddlewareInjectToken.Config, {
            useFactory: () => this.config,
        });

        container.register(MiddlewareInjectToken.Cache, MemoryCache);

        container.register(MiddlewareInjectToken.OrbisSdk, {
            useFactory: () => this.orbisSdk,
        });

        container.register(MiddlewareInjectToken.MiddlewareSdk, {
            useFactory: () => this.middlewareSdk,
        });

        container.register(MiddlewareInjectToken.Sdks, {
            useFactory: instanceCachingFactory<Sdks>((container) => ({
                orbis: container.resolve(OrbisSdkWrapper),
                middleware: container.resolve(MiddlewareInjectToken.MiddlewareSdk),
            })),
        });

        container.registerSingleton(
            ApiInjectToken.BranchApi,
            delay(() => BranchApi),
        );
        container.registerSingleton(ApiInjectToken.ModelApi, ModelApi);
        container.registerSingleton(ApiInjectToken.GroupApi, GroupApi);
        container.registerSingleton(ApiInjectToken.UserApi, UserApi);
        container.registerSingleton(ApiInjectToken.AccountApi, isAxos ? AxosAccountApi : AccountApi);
        container.registerSingleton(ApiInjectToken.MarketApi, MarketApi);
        container.registerSingleton(ApiInjectToken.TradingApi, isAxos ? AxosTradingApi : TradingApi);
        container.registerSingleton(ApiInjectToken.QuoteApi, isAxos ? AxosQuoteApi : QuoteApi);
        container.registerSingleton(ApiInjectToken.WatchlistApi, WatchlistApi);
        container.registerSingleton(ApiInjectToken.ResearchApi, ResearchApi);
        container.registerSingleton(ApiInjectToken.StatApi, StatApi);
        container.registerSingleton(ApiInjectToken.NewsApi, NewsApi);
        container.registerSingleton(ApiInjectToken.TipranksApi, TipranksApi);
        container.registerSingleton(ApiInjectToken.WebSocketApi, WebSocketApi);
        container.registerSingleton(ApiInjectToken.DocumentApi, DocumentApi);
    }

    initialize(config: DeepPartial<MiddlewareConfig>) {
        this.config = {
            ...this.config,
            ...config,
            apiPermissions: {
                ...this.config.apiPermissions,
                ...config.apiPermissions,
            },
        };

        container.reset();
        this.registerDependencies();

        this.branch = container.resolve(ApiInjectToken.BranchApi);
        this.model = container.resolve(ApiInjectToken.ModelApi);
        this.group = container.resolve(ApiInjectToken.GroupApi);
        this.user = container.resolve(ApiInjectToken.UserApi);
        this.account = container.resolve(ApiInjectToken.AccountApi);
        this.market = container.resolve(ApiInjectToken.MarketApi);
        this.trading = container.resolve(ApiInjectToken.TradingApi);
        this.quote = container.resolve(ApiInjectToken.QuoteApi);
        this.watchlist = container.resolve(ApiInjectToken.WatchlistApi);
        this.research = container.resolve(ApiInjectToken.ResearchApi);
        this.stat = container.resolve(ApiInjectToken.StatApi);
        this.news = container.resolve(ApiInjectToken.NewsApi);
        this.tipranks = container.resolve(ApiInjectToken.TipranksApi);
        this.webSocket = container.resolve(ApiInjectToken.WebSocketApi);
        this.document = container.resolve(ApiInjectToken.DocumentApi);
    }

    getSdks() {
        return container.resolve<Sdks>(MiddlewareInjectToken.Sdks);
    }
}

export default OrbisApiMiddleware;
